import { useState, useEffect } from 'react'
import { Link2 } from 'lucide-react'
import { useUserData } from '@/hooks/useUserData'
import { useAuthState } from '@/hooks/useAuth'
import { getPlatform, isNative } from '@/lib/platform'
import { Keyboard } from '@capacitor/keyboard'
import { nanoid } from 'nanoid'
import { db } from '@/lib/firebase'
import { collection, addDoc, Timestamp } from 'firebase/firestore'

interface ShareBookingLinkDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Generate time options: 1 to 24 hours in 0.5 increments
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = (i + 2) * 0.5
  return {
    value: hours,
    label: hours % 1 === 0 ? `${hours.toFixed(0)}h` : `${hours}h`
  }
})

export function ShareBookingLinkDialog({
  isOpen,
  onOpenChange,
}: ShareBookingLinkDialogProps) {
  const { user } = useAuthState()
  const { artistId, isArtist } = useUserData()
  const [copied, setCopied] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [generatedLinkId, setGeneratedLinkId] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  
  // Form state
  const [estimatedTime, setEstimatedTime] = useState<number | 'unknown'>(1)
  const [preparationDays, setPreparationDays] = useState<number>(0)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedLinkId(null)
      setEstimatedTime(1)
      setPreparationDays(0)
      setCopied(false)
      setErrorMessage(null)
    }
  }, [isOpen])

  // Keyboard tracking for iOS native - only run on iOS to avoid double keyboard handling
  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: any
    let hideListener: any

    const setupKeyboardListeners = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setupKeyboardListeners()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  // Calculate safe top position based on platform
  const getModalTop = () => {
    if (keyboardHeight > 0) {
      return `calc((100dvh - ${keyboardHeight}px) / 2)`
    }
    // Use 100dvh for dynamic viewport height on mobile
    return isNative() ? 'calc(100dvh / 2)' : '50%'
  }

  const generateBookingLink = async () => {
    setErrorMessage(null)
    
    // Validate user is authenticated
    if (!user) {
      const msg = 'You must be logged in to generate booking links.'
      console.error('[ShareBookingLinkDialog]', msg)
      setErrorMessage(msg)
      return
    }
    
    // Validate user is an artist
    if (!isArtist) {
      const msg = 'Only artists can generate booking links.'
      console.error('[ShareBookingLinkDialog]', msg)
      setErrorMessage(msg)
      return
    }
    
    // Validate artistId exists
    if (!artistId) {
      const msg = 'Artist ID not found. Please ensure your artist profile is set up correctly.'
      console.error('[ShareBookingLinkDialog]', msg)
      setErrorMessage(msg)
      return
    }
    
    setIsGenerating(true)
    
    try {
      // Generate unique linkId
      const linkId = nanoid(12) // 12 characters for good uniqueness
      
      const bookingLinkData = {
        linkId,
        artistId,
        estimatedTime: estimatedTime === 'unknown' ? 'unknown' : estimatedTime,
        preparationDays: preparationDays,
        createdAt: Timestamp.now(),
        isActive: true,
        expiresAt: null,
        metadata: {}
      }
      
      console.log('[ShareBookingLinkDialog] Creating booking link with data:', { 
        userId: user.uid,
        linkData: bookingLinkData
      })
      
      // Create booking link document in Firestore
      const docRef = await addDoc(collection(db, 'bookingLinks'), bookingLinkData)
      
      console.log('[ShareBookingLinkDialog] Booking link created successfully:', {
        docId: docRef.id,
        linkId: linkId
      })
      setGeneratedLinkId(linkId)
    } catch (err) {
      console.error('[ShareBookingLinkDialog] Failed to generate booking link:', err)
      
      // Log more details about the error
      if (err instanceof Error) {
        console.error('[ShareBookingLinkDialog] Error details:', {
          message: err.message,
          code: (err as any).code,
          stack: err.stack
        })
      }
      
      const msg = err instanceof Error ? err.message : 'Failed to generate link. Please try again.'
      setErrorMessage(msg)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopyLink = async () => {
    if (!generatedLinkId) return
    
    const bookingLink = `${window.location.origin}/book/${generatedLinkId}`
    
    try {
      await navigator.clipboard.writeText(bookingLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }



  if (!mounted || !isOpen) return null
  
  // Show error message if user is not an artist
  if (isOpen && !isArtist) {
    return (
      <>
        {/* Overlay */}
        <div
          className="fixed inset-0 z-[55] bg-black/50 animate-in fade-in-0"
          onClick={() => onOpenChange(false)}
        />

        {/* Modal */}
        <div 
          className="fixed left-0 right-0 z-[60] flex items-center justify-center pointer-events-none p-4"
          style={{
            top: getModalTop(),
            transform: 'translateY(-50%)',
            transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Not Available</h2>
            <p className="text-sm text-foreground/80">
              Only artists can create booking links. Please log in as an artist to continue.
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </>
    )
  }
  
  if (!artistId) return null

  const bookingLink = generatedLinkId ? `${window.location.origin}/book/${generatedLinkId}` : ''

  return (
    <>
      {/* Overlay - covers full screen including notch area */}
      <div
        className="fixed inset-0 z-[55] bg-black/50 animate-in fade-in-0"
        onClick={() => onOpenChange(false)}
      />

      {/* Modal */}
      <div 
        className="fixed left-0 right-0 z-[60] flex items-center justify-center pointer-events-none p-4"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="share-booking-link-title"
        aria-describedby="share-booking-link-description"
        style={{
          top: getModalTop(),
          transform: 'translateY(-50%)',
          transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto max-h-[90vh] overflow-y-auto">
          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Header with Icon and Title */}
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-foreground flex-shrink-0" />
              <h2 id="share-booking-link-title" className="text-xl font-semibold text-foreground">
                {generatedLinkId ? 'Share Your Booking Link' : 'Create Booking Link'}
              </h2>
            </div>

            {/* Description */}
            <div id="share-booking-link-description" className="space-y-3">
              <p className="text-sm text-foreground/80 leading-relaxed">
                Create a unique booking link for a client.
              </p>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                <p className="text-sm text-destructive">{errorMessage}</p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Estimated Time Select */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Estimated time for this session?
                </label>
                <select
                  value={estimatedTime === 'unknown' ? 'unknown' : estimatedTime}
                  onChange={(e) => {
                    const val = e.target.value
                    setEstimatedTime(val === 'unknown' ? 'unknown' : parseFloat(val))
                  }}
                  disabled={!!generatedLinkId}
                  className="w-full px-3 py-2 text-base text-foreground bg-background border border-input rounded-md focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="unknown">Unknown</option>
                  {TIME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preparation Days Select */}
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-foreground">
                    How many days do you need to prepare?
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Starting from the time it's booked
                  </p>
                </div>
                <select
                  value={preparationDays}
                  onChange={(e) => setPreparationDays(parseInt(e.target.value))}
                  disabled={!!generatedLinkId}
                  className="w-full px-3 py-2 text-base text-foreground bg-background border border-input rounded-md focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="0">Same day</option>
                  <option value="1">1 day</option>
                  <option value="2">2 days</option>
                  <option value="3">3 days</option>
                  <option value="4">4 days</option>
                  <option value="5">5 days</option>
                  <option value="6">6 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="21">21 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </div>

            {/* Generate Link Button */}
            <button
              type="button"
              onClick={generateBookingLink}
              disabled={isGenerating || !!generatedLinkId}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? 'Generating...' : generatedLinkId ? 'Generated!' : 'Generate Link'}
            </button>

            {/* Link Display - Shows after generation */}
            {generatedLinkId && (
              <div className="flex items-center gap-3">
                <code className="flex-1 text-sm text-foreground break-all font-mono">
                  {bookingLink}
                </code>
                <button
                  onClick={handleCopyLink}
                  className="flex-shrink-0 p-1.5 hover:bg-muted rounded transition-colors"
                  title={copied ? 'Copied!' : 'Copy link'}
                  type="button"
                >
                  {copied ? (
                    <svg 
                      className="w-5 h-5 text-primary animate-in spin-in-90 duration-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg 
                      className="w-5 h-5 text-foreground/60 hover:text-foreground transition-colors" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {/* Close Button - Only show before generation */}
            {!generatedLinkId && (
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full bg-muted hover:bg-muted/80 text-foreground font-semibold py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

    </>
  )
}
